// 📂 src/backend/firebase/hooks/useClient.ts

'use client';

import { useAuth } from '@/backend/context/auth-management';
import type { Client, ClientInput, ClientWithId } from '@/shared/schema';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ClientService } from '../services/client-service';
import { APP_KEYS } from './query-keys';

/**
 * 🚀 HELPER: Determina a quién le pertenecen los datos.
 * TufirmaPro ahora se centra en el dueño del negocio.
 */
const useTargetId = () => {
  const profile = useAuth((s) => s.profile);
  return profile?.id;
};

export const useClients = {
  // ============================================
  // QUERIES
  // ============================================

  getAll: (ownerId?: string) => {
    const hookId = useTargetId();
    const targetId = ownerId || hookId;

    return useQuery<ClientWithId[]>({
      queryKey: APP_KEYS.clients.list(targetId!),
      queryFn: () => ClientService.getAll(targetId!),
      enabled: !!targetId,
    });
  },

  getById: (clientId: string | undefined) => {
    const targetId = useTargetId();

    return useQuery<ClientWithId | null>({
      queryKey: APP_KEYS.clients.detail(targetId!, clientId!),
      queryFn: () => ClientService.getById(targetId!, clientId!),
      enabled: !!targetId && !!clientId,
    });
  },

  getByPhoneNumber: (
    phoneNumber: string,
    options?: Omit<
      UseQueryOptions<ClientWithId[], Error>,
      'queryKey' | 'queryFn'
    >,
  ) => {
    const targetId = useTargetId();

    return useQuery({
      queryKey: [...APP_KEYS.clients.owner(targetId!), 'phone', phoneNumber],
      queryFn: () => ClientService.getByPhoneNumber(targetId!, phoneNumber),
      enabled: !!targetId && !!phoneNumber,
      ...options,
    });
  },

  // ============================================
  // MUTATIONS
  // ============================================

  create: () => {
    const queryClient = useQueryClient();
    const targetId = useTargetId();

    return useMutation({
      mutationFn: (data: ClientInput) => ClientService.create(targetId!, data),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.clients.owner(targetId!),
        });
      },
    });
  },

  update: () => {
    const queryClient = useQueryClient();
    const targetId = useTargetId();

    return useMutation({
      mutationFn: ({
        clientId,
        data,
      }: {
        clientId: string;
        data: Partial<Client>;
      }) => ClientService.update(targetId!, clientId, data),
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.clients.owner(targetId!),
        });
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.clients.detail(targetId!, variables.clientId),
        });
      },
    });
  },

  delete: () => {
    const queryClient = useQueryClient();
    const targetId = useTargetId();

    return useMutation({
      mutationFn: (clientId: string) =>
        ClientService.delete(targetId!, clientId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.clients.owner(targetId!),
        });
      },
    });
  },

  getDeleted: () => {
    const targetId = useTargetId();

    return useQuery<ClientWithId[]>({
      queryKey: [...APP_KEYS.clients.owner(targetId || 'no-owner'), 'deleted'],
      queryFn: () => {
        if (!targetId) return [];
        return ClientService.getDeleted(targetId);
      },
      enabled: !!targetId,
    });
  },

  restore: () => {
    const queryClient = useQueryClient();
    const targetId = useTargetId();

    return useMutation({
      mutationFn: (clientId: string) =>
        ClientService.restore(targetId!, clientId),
      onSuccess: () => {
        if (!targetId) return;
        queryClient.invalidateQueries({
          queryKey: APP_KEYS.clients.owner(targetId),
        });
        queryClient.invalidateQueries({
          queryKey: [...APP_KEYS.clients.owner(targetId), 'deleted'],
        });
      },
    });
  },
};
